import classNames from "classnames";
import React, { useEffect, useId, useState } from "react";

import ODSColumn from "../ODSColumn/ODSColumn";
import ODSIcon from "../ODSIcons/ODSIcon";
import ODSRow from "../ODSRow/ODSRow";
import { ODSAccordionProps } from "./ODSAccordion.types";
import ODSAccordionSummary from "./ODSAccordionSummary";

import "./css/index.css";

const ODSAccordion: React.FC<ODSAccordionProps> = ({
  size = "small",
  disabled = false,
  onToggle,
  className,
  expanded = false,
  contentSlot,
  contentText,
  headerText,
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);

  useEffect(() => setIsExpanded(expanded), [expanded]);

  useEffect(() => {
    if (contentText) {
      console.warn(
        "The `contentText` prop for Accordion is deprecated. Please use `contentSlot` instead."
      );
    }
  }, [contentText]);

  const handleToggle = () => {
    setIsExpanded((prevExpanded) => !prevExpanded);
    if (onToggle) {
      onToggle(!isExpanded);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ([" ", "Enter"].includes(event.key)) {
      event.preventDefault();
      handleToggle();
    }
  };

  const componentId = useId();
  const buttonId = `ODSAccordion-Summary-${componentId}`;
  const contentFrameId = `ODSAccordion-ContentFrame-${componentId}`;

  return (
    <ODSColumn
      className={classNames(
        `ODSAccordion`,
        isExpanded && `ODSAccordion--expanded`,
        disabled && `ODSAccordion--disabled`,
        `ODSAccordion--expanded-${isExpanded}--disabled-${disabled}`,
        `ODSAccordion--size-${size}`,
        className
      )}
      {...rest}
    >
      <button
        id={buttonId}
        tabIndex={0}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isExpanded}
        aria-controls={contentFrameId}
        className={classNames(
          `ODSAccordion-HeaderIcon`,
          disabled && `ODSAccordion-HeaderIcon--disabled`,
          `ODSAccordion-HeaderIcon--size-${size}`
        )}
      >
        <ODSAccordionSummary size={size}>{headerText}</ODSAccordionSummary>
        <ODSRow className={classNames(`ODSAccordion-ExpandAndCollapseIcon`)}>
          <ODSIcon
            name={
              isExpanded
                ? "collapse-up-type-standard"
                : "collapse-down-type-standard"
            }
            decorative
            className={classNames(
              `${
                isExpanded
                  ? `ODSAccordion-CollapseUp ODSAccordion-CollapseUp--size-${size}`
                  : `ODSAccordion-CollapseDown ODSAccordion-CollapseDown--size-${size}`
              }`
            )}
          ></ODSIcon>
        </ODSRow>
      </button>
      <div
        id={contentFrameId}
        className={classNames(
          `ODSAccordion-ContentFrame`,
          isExpanded && `ODSAccordion-ContentFrame--expanded`
        )}
        role="region"
        aria-labelledby={buttonId}
        aria-label={`${headerText} Content`}
        hidden={!isExpanded}
      >
        <span
          id={`ODSAccordion-ContentSpan-${componentId}`}
          className="sr-only"
        >
          {headerText} content
        </span>
        {contentSlot}
        {contentText}
      </div>
    </ODSColumn>
  );
};

export default ODSAccordion;
